/**
 * GB組織74問 個人レポート派生値の算出 (gb-org-questionnaire-74)
 *
 * AnalysisResult.scores (gb74形式) から、個人レポート
 * (docs/mockups/12_モック_GB人財_ダミーレポート.html) に必要な派生値を算出する。
 *
 * 【確定情報】設問一覧 (GBbrain_最終確定設問一覧.md) に基づく:
 *  - Big5因子→設問の対応、Validity逆転項目 (B-12/B-21)、Shadow項目 (B-04/08/15/21)
 *  - ダイヤモンド4軸 = thinkingType A(しくみ)/B(守る)/C(ひと)/D(変える) の個数 (各軸最大12)
 *  - 論理性 = skill (SK-01〜04) の平均
 *
 * 【確定仕様準拠】(01_マスター仕様書§124-150 / 04_コントラスト分岐文言テンプレート):
 *  - タイプ判定: 7点優勢閾値で 4象限/対角/1軸/3軸/全方型 に分類 (classifyPattern)
 *  - コントラスト判定: スプレッド≧6 または 10点突破 → 鮮明、それ以外 → 穏やか (getContrastLevel)
 *  - タグライン/思考/行動/気をつけたい/補完の相手: gb74ContrastText.ts (正本04) を配線
 *
 * 【仮ロジック】算出仕様が未定義のため暫定実装 (確定仕様が出たら差し替え):
 *  - 役割適性6項目の算出式 (Big5因子・軸の重み付け)
 *  - Shadow文 (B-xx個別テンプレート)、軸別スタイル文 (1軸/3軸/対角の思考・行動の補完用)
 */

import {
  CONTRAST_PATTERNS,
  contrastFromSpread,
  getContrastSections,
  pairToPatternKey,
  type ContrastLevel,
  type PatternCategory,
} from './gb74ContrastText';
import type { JudgmentPattern } from '../api/analysisApi';

/** gb74 scores 形式 (scoresCompat.Gb74Scores と同等) */
export interface Gb74Scores {
  version: 'gb74';
  thinkingType: Record<string, number>; // A/B/C/D の個数
  big5: Record<string, number>; // B-01〜B-30: 1〜5
  skill: Record<string, number>; // SK-01〜SK-04: 1〜5
  ona: Record<string, number>; // O-01〜O-10: 1〜5
  onaLevel1: Record<string, number>; // O-11: 1〜4, O-12: 1〜3
  onaDept: Record<string, number>; // departmentId: 1〜4
  retention: Record<string, number>; // R-01〜R-03: 1〜5
}

/** ダイヤモンド軸 (思考タイプ) の表示メタ情報 */
export const AXIS_META = {
  A: { key: 'A', label: 'システム', short: 'しくみ', color: '#3B82F6' },
  B: { key: 'B', label: '安定', short: '守る', color: '#8B5CF6' },
  C: { key: 'C', label: '人', short: 'ひと', color: '#EC4899' },
  D: { key: 'D', label: '変化', short: '変える', color: '#F59E0B' },
} as const;

export type AxisKey = keyof typeof AXIS_META;

/**
 * 個人レポート§01「あなたのダイヤモンド」象限別の表示コンテンツ (design_images/個人.png 準拠)。
 * 表示ラベルは基本方針 (A 論理 / B 着実 / C 共感 / D 創造) に従う本レポート専用の定義。
 * 組織レポート等と共有する AXIS_META.label (安定 等) はここでは改名しない。
 */
export interface AxisReportContent {
  letter: AxisKey;
  /** 軸ラベル (論理/着実/共感/創造) */
  axisLabel: string;
  /** タイプ見出し (論理タイプ/着実タイプ/共感タイプ/創造タイプ) */
  typeLabel: string;
  /** 特徴の箇条書き (●3行) */
  bullets: string[];
  /** タグ (chip 4つ) */
  chips: string[];
}

export const AXIS_REPORT: Record<AxisKey, AxisReportContent> = {
  A: {
    letter: 'A',
    axisLabel: '論理',
    typeLabel: '論理タイプ',
    bullets: [
      'データに基づき合理的に考える',
      '情報を整理し結論を導く',
      '曖昧さを嫌い明確さを求める',
    ],
    chips: ['事実重視', '数量的', '理性的', '論理的'],
  },
  B: {
    letter: 'B',
    axisLabel: '着実',
    typeLabel: '着実タイプ',
    bullets: ['効率重視で実務を遂行', '計画性と管理力に長ける', '秩序を守り冷静に行動'],
    chips: ['保守・形式的', '実直', '感情抑制', '順序立つ'],
  },
  C: {
    letter: 'C',
    axisLabel: '共感',
    typeLabel: '共感タイプ',
    bullets: ['人との関わりや表現が得意', '気配りでチームを支える', '感性豊かで共感力が高い'],
    chips: ['感覚・感情的', '音楽的', '直感的', '話好き'],
  },
  D: {
    letter: 'D',
    axisLabel: '創造',
    typeLabel: '創造タイプ',
    bullets: [
      '広い視野で発想が豊か',
      '独自の発想で新しい可能性を生む',
      '変化を楽しみ、挑戦に前向き',
    ],
    chips: ['想像力豊か', '芸術的', '空間的', '未来志向'],
  },
};

/**
 * GB-Type コード算出 (design_images/個人.png のヘッダー「〇 タイプ」)。
 * axis は Part1 の思考タイプ個数 (各軸0〜12, 全回答時は合計24)。
 *
 *  条件1: 4値がすべて {6,7,8} のみ → 'ABCDタイプ'
 *  条件2: 降順に並べ、10以上の軸は S 記号 (SA/SB/SC/SD)
 *  条件3: (条件1以外で) 7〜9 の軸は記号 (A/B/C/D)
 *  条件4: 未確定なら条件2・3の結果を降順に連結し 'タイプ' を付す
 *
 * ※ 合計24制約より、最大軸≤6なら全軸=6 (条件1)、5以下があれば必ず別軸≥7 (条件3) となるため
 *   通常データで未確定は発生しない。欠損データ用に最上位1軸のフォールバックのみ用意する。
 */
export function computeGbType(axis: Record<AxisKey, number>): string {
  const keys: AxisKey[] = ['A', 'B', 'C', 'D'];
  const vals = keys.map((k) => axis[k] ?? 0);
  // 条件1: すべて 6,7,8 のみ
  if (vals.every((v) => v >= 6 && v <= 8)) return 'ABCDタイプ';
  // 大きい順 (同点は A,B,C,D 順)
  const ordered = [...keys].sort(
    (a, b) => (axis[b] ?? 0) - (axis[a] ?? 0) || keys.indexOf(a) - keys.indexOf(b)
  );
  const parts: string[] = [];
  for (const k of ordered) {
    const v = axis[k] ?? 0;
    if (v >= AXIS_BREAKTHROUGH)
      parts.push(`S${k}`); // 条件2
    else if (v >= 7 && v <= 9) parts.push(k); // 条件3
  }
  if (parts.length > 0) return `${parts.join('')}タイプ`; // 条件4
  // 防御的フォールバック (通常データでは到達しない)
  return `${ordered[0]}タイプ`;
}

/** Part1 各軸の最大個数 (各軸が登場するペア数 × 4問。①A/C ②B/D ③A/D ④A/B ⑤C/D ⑥C/B) */
export const AXIS_MAX = 12;
/** ダイヤモンドの基準線 (これを超えると「突破」) */
export const AXIS_BREAKTHROUGH = 10;

/** Big5因子の定義 (設問一覧の因子→設問対応。確定情報) */
export interface Big5Factor {
  key: string;
  name: string;
  /** 構成設問コード (B-xx) */
  questions: string[];
  /** 因子の説明 (強みとして提示する文。仮テンプレート) */
  strengthDesc: string;
}

export const BIG5_FACTORS: Big5Factor[] = [
  {
    key: 'initiative',
    name: '主導性',
    questions: ['B-01', 'B-02', 'B-03', 'B-04'],
    strengthDesc:
      '自分から「こうしよう」と提案し、周囲を動かす力があります。決断を待つよりも自ら動くタイプです。',
  },
  {
    key: 'execution',
    name: '遂行力',
    questions: ['B-05', 'B-06', 'B-07', 'B-08'],
    strengthDesc:
      '始めた仕事を最後までやり遂げ、細部まで正確に仕上げる力があります。任せた仕事を確実に形にします。',
  },
  {
    key: 'consideration',
    name: '配慮性',
    questions: ['B-09', 'B-10', 'B-11', 'B-12'],
    strengthDesc:
      '相手の気持ちを自然に察し、チームの雰囲気の変化に素早く気づきます。人の力を引き出す土台になっています。',
  },
  {
    key: 'challenge',
    name: '挑戦性',
    questions: ['B-13', 'B-14', 'B-15'],
    strengthDesc:
      'やったことがないことに自ら飛び込む力があります。リスクがあっても面白そうなら動ける人です。',
  },
  {
    key: 'order',
    name: '秩序性',
    questions: ['B-16', 'B-17', 'B-18'],
    strengthDesc:
      '物事を順序立てて整理し、計画を立ててから動く力があります。混乱した状況に道筋をつけられます。',
  },
  {
    key: 'emotionControl',
    name: '感情制御',
    questions: ['B-19', 'B-20', 'B-21'],
    strengthDesc:
      '感情的になっても素早く切り替え、厳しい場面でも冷静に判断できます。場を落ち着かせる存在です。',
  },
  {
    key: 'integrity',
    name: '誠実対人性',
    questions: ['B-22', 'B-23', 'B-24'],
    strengthDesc:
      '小さな約束でも必ず守り、相手の立場に立って考えます。周囲から信頼を集める誠実さがあります。',
  },
  {
    key: 'sociability',
    name: '社交性',
    questions: ['B-25', 'B-26'],
    strengthDesc:
      '部署や立場の違う相手にも気軽に話しかけられます。人と人をつなぐ起点になれる人です。',
  },
  {
    key: 'curiosity',
    name: '知的好奇心',
    questions: ['B-27', 'B-28'],
    strengthDesc:
      '新しい知識を学ぶことや「なぜそうなるのか」を深く考えることを楽しめます。視野を広げ続けられる人です。',
  },
  {
    key: 'stability',
    name: '情緒安定性',
    questions: ['B-29', 'B-30'],
    strengthDesc:
      '気分の浮き沈みが少なく、小さなことを引きずりません。安定して力を発揮し続けられます。',
  },
];

/** Validity逆転項目 (スコアを 6 - value で反転して因子に算入) */
const REVERSED_QUESTIONS = new Set(['B-12', 'B-21']);

/** Shadow項目 (高スコアほど「気をつけたいこと」の兆候) */
const SHADOW_QUESTIONS: Array<{ code: string; title: string; text: string }> = [
  {
    code: 'B-04',
    title: '押し切りの兆候',
    text: '周囲の意見を聞かずに自分のやり方を押し通すことがあります。動く前に一度周囲に声をかける習慣が効果的です。',
  },
  {
    code: 'B-08',
    title: '抱え込みの兆候',
    text: '人に任せず自分でやってしまう傾向があります。意識して任せる仕組みをつくると、強みがさらに活きます。',
  },
  {
    code: 'B-15',
    title: '拡散の兆候',
    text: '面白そうなことに次々と手を出し、同時に複数を走らせる傾向があります。取り組むテーマを1〜2つに絞ると効果的です。',
  },
  {
    code: 'B-21',
    title: '抱え込み(対人回避)の兆候',
    text: 'ストレスを感じると周りと話すのを避けてしまうことがあります。早めに状況を共有できる相手をつくると安心です。',
  },
];

// ===== 算出ロジック =====

/** scores のネスト ({scores: {...}}) を吸収して gb74 オブジェクトを取り出す */
export function unwrapGb74Scores(raw: unknown): Gb74Scores | null {
  if (!raw || typeof raw !== 'object') return null;
  let obj = raw as Record<string, unknown>;
  // {scores: {...}} の二重ネストを1段ほどく
  if (
    !('version' in obj) &&
    'scores' in obj &&
    typeof obj.scores === 'object' &&
    obj.scores !== null
  ) {
    obj = obj.scores as Record<string, unknown>;
  }
  if (obj.version !== 'gb74') return null;
  return obj as unknown as Gb74Scores;
}

/** ダイヤモンド4軸スコア (生の個数、0〜12) */
export function getAxisScores(scores: Gb74Scores): Record<AxisKey, number> {
  const t = scores.thinkingType ?? {};
  return {
    A: t.A ?? 0,
    B: t.B ?? 0,
    C: t.C ?? 0,
    D: t.D ?? 0,
  };
}

/** 1因子のスコア (構成設問の平均、1〜5。逆転項目は反転) */
export function getFactorScore(scores: Gb74Scores, factor: Big5Factor): number {
  const vals = factor.questions
    .map((code) => {
      const v = scores.big5?.[code];
      if (v == null) return null;
      return REVERSED_QUESTIONS.has(code) ? 6 - v : v;
    })
    .filter((v): v is number => v != null);
  if (vals.length === 0) return 0;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/** 全因子のスコア一覧 (降順ソート) */
export function getFactorScores(scores: Gb74Scores): Array<{ factor: Big5Factor; score: number }> {
  return BIG5_FACTORS.map((factor) => ({ factor, score: getFactorScore(scores, factor) })).sort(
    (a, b) => b.score - a.score
  );
}

/** 論理性 (skill SK-01〜04 平均、1〜5) と判定ランク */
export function getLogicScore(scores: Gb74Scores): {
  score: number;
  rank: 'good' | 'normal' | 'low';
} {
  const vals = ['SK-01', 'SK-02', 'SK-03', 'SK-04']
    .map((c) => scores.skill?.[c])
    .filter((v): v is number => v != null);
  const score = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  const rank = score >= 4 ? 'good' : score >= 2.5 ? 'normal' : 'low';
  return { score, rank };
}

/** プライマリー(優勢)判定の閾値 (01_マスター仕様書§220: 7点以上=優勢) */
export const PRIMARY_THRESHOLD = 7;
/** ターシャリー判定の閾値 (3点以下) */
export const TERTIARY_THRESHOLD = 3;

/** タイプ分類の結果 */
export interface PatternResult {
  /** gb74ContrastText のパターンキー (AB/AD/BC/CD/AC/BD/A〜D/ABD等/ALL) */
  key: string;
  /** 表示タイプ名 */
  typeName: string;
  /** 分類カテゴリ */
  category: PatternCategory;
  /** 最も強い軸 (チャートの主軸表示用) */
  primaryAxis: AxisKey;
  /** 軸構成の説明 (例: 「人 × 変化」) */
  sub: string;
  /** triple のみ: 相対的に低い1軸 */
  lowAxis?: AxisKey;
}

/**
 * 4軸スコア(0〜12)からパターンを分類する (01_マスター仕様書§124-150 準拠)。
 * 7点以上をプライマリー(優勢軸)とし、優勢軸の本数・隣接/対角で分類する。
 *  - 0優勢かつ0ターシャリー(全軸4〜6) → 全方型(ALL)
 *  - 1優勢 → 1軸 / 2優勢(隣接) → 4象限 / 2優勢(対角) → 対角型 / 3優勢 → 3軸
 *  - 0優勢(ターシャリーあり) / 4優勢 のレアケースは相対上位2軸で代替
 */
export function classifyPattern(axis: Record<AxisKey, number>): PatternResult {
  const keys: AxisKey[] = ['A', 'B', 'C', 'D'];
  const byScoreDesc = [...keys].sort((a, b) => axis[b] - axis[a]);
  const primaries = keys.filter((k) => axis[k] >= PRIMARY_THRESHOLD);
  const tertiaries = keys.filter((k) => axis[k] <= TERTIARY_THRESHOLD);
  const strongest = byScoreDesc[0];
  const label = (k: AxisKey) => AXIS_META[k].label;

  // 全方型: 優勢もターシャリーも無い (全軸4〜6)
  if (primaries.length === 0 && tertiaries.length === 0) {
    return {
      key: 'ALL',
      typeName: CONTRAST_PATTERNS.ALL.typeName,
      category: 'balanced',
      primaryAxis: strongest,
      sub: '4軸バランス型',
    };
  }

  // 優勢軸セット (1〜3本ならそれ、0本/4本のレアケースは相対上位2軸)
  const dom =
    primaries.length >= 1 && primaries.length <= 3
      ? [...primaries].sort((a, b) => axis[b] - axis[a])
      : byScoreDesc.slice(0, 2);

  // 1軸優勢
  if (dom.length === 1) {
    const k = dom[0];
    return {
      key: k,
      typeName: CONTRAST_PATTERNS[k].typeName,
      category: 'single',
      primaryAxis: k,
      sub: `${label(k)} 単独優勢`,
    };
  }

  // 3軸優勢
  if (dom.length === 3) {
    const low = keys.find((k) => !dom.includes(k)) as AxisKey;
    const key = keys.filter((k) => k !== low).join(''); // A,B,C,D順 (例: ABD)
    const entry = CONTRAST_PATTERNS[key];
    return {
      key,
      typeName: entry?.typeName ?? `${key}型`,
      category: 'triple',
      primaryAxis: dom[0],
      sub: `${dom.map(label).join('・')} の3軸優勢`,
      lowAxis: low,
    };
  }

  // 2軸優勢 → 隣接(4象限) or 対角
  const [first, second] = dom;
  const key = pairToPatternKey(first, second);
  if (key) {
    const entry = CONTRAST_PATTERNS[key];
    return {
      key,
      typeName: entry.typeName,
      category: entry.category,
      primaryAxis: first,
      sub: `${label(first)} × ${label(second)}`,
    };
  }
  // フォールバック (理論上到達しない)
  return {
    key: first,
    typeName: CONTRAST_PATTERNS[first].typeName,
    category: 'single',
    primaryAxis: first,
    sub: `${label(first)} 単独優勢`,
  };
}

/** コントラスト水準を判定 (04§6: スプレッド≧6 または 10点突破 → 鮮明) */
export function getContrastLevel(axis: Record<AxisKey, number>): ContrastLevel {
  const vals = [axis.A, axis.B, axis.C, axis.D];
  const spread = Math.max(...vals) - Math.min(...vals);
  const hasBreakthrough = vals.some((v) => v >= AXIS_BREAKTHROUGH);
  return contrastFromSpread(spread, hasBreakthrough);
}

/** タイプ名 (分類器のラッパー。後方互換のため {name, sub, primaryAxis} を返す) */
export function getTypeName(axis: Record<AxisKey, number>): {
  name: string;
  sub: string;
  primaryAxis: AxisKey;
} {
  const p = classifyPattern(axis);
  return { name: p.typeName, sub: p.sub, primaryAxis: p.primaryAxis };
}

/** 役割適性6項目 (仮ロジック: Big5因子・軸の重み付け、0〜100) */
export interface RoleFitness {
  name: string;
  score: number;
}

/**
 * 役割適性の構成表。
 *
 * 【⚠️ 暫定。廣瀬様の確認待ち】
 * 検証のため先に実装したもので、確定仕様ではありません。
 * 指摘があった場合、修正するのは原則この表と下の重み定数だけで済みます
 * （算出関数・レポート側は触らない）。
 *
 * 【この構成にした理由】
 *  - 主要因子を6役割すべて別にした。旧構成は3要素中2つを共有するペアがあり、
 *    「管理統率」と「承継適性」の相関が 0.72 と実質同じ指標になっていた（修正後 0.17）。
 *  - 旧構成では A軸（論理）・感情制御・知的好奇心・GBスキル論理性 が未使用だった。
 *    修正後は Big5 10因子すべて・思考軸4つすべて・論理性を使う。
 *  - 「安定運用」は情緒の安定した人が向く役割なので情緒安定性を、
 *    対人場面で感情を抑える力である感情制御は「協働調整」に置いた。
 *    （依頼文には安定運用＝感情制御と誤記していた。両方とも使うので捨てる必要はない）
 *
 * 補助因子に 'LOGIC' を指定した場合は Big5 ではなく GBスキルの論理性(SK-01〜04)を使う。
 */
export const ROLE_DEFINITIONS: Array<{
  name: string;
  primary: string;
  secondary: string;
  axis: AxisKey;
}> = [
  { name: '変革推進', primary: 'challenge', secondary: 'curiosity', axis: 'D' },
  { name: '橋渡し', primary: 'sociability', secondary: 'integrity', axis: 'C' },
  { name: '協働調整', primary: 'consideration', secondary: 'emotionControl', axis: 'C' },
  { name: '管理統率', primary: 'initiative', secondary: 'LOGIC', axis: 'A' },
  { name: '承継適性', primary: 'execution', secondary: 'integrity', axis: 'B' },
  { name: '安定運用', primary: 'order', secondary: 'stability', axis: 'B' },
];

/** 役割スコアの重み（主要因子・補助因子・思考軸）。合計 1.0。⚠️ 暫定値。 */
export const ROLE_WEIGHT_PRIMARY = 0.5;
export const ROLE_WEIGHT_SECONDARY = 0.3;
export const ROLE_WEIGHT_AXIS = 0.2;

/**
 * 役割適性6項目を 0〜100 で算出し、降順で返す。
 *
 * スコア = 主要因子×0.5 + 補助因子×0.3 + 思考軸×0.2（いずれも 0〜1 に正規化）
 *
 * 【この数値の限界】設問数が1因子あたり2〜4問しかないため、順位の信頼性は約70%。
 * 観測1位が本人の真の1位と一致しない人が約3割いる。式を変えても改善しない
 * （設問を2倍にしても76%）。そのため表示側では数値と順位を出さず、
 * 「向いていそうな役割」3つ／「補うとよい役割」3つのグループとして提示している。
 */
export function getRoleFitness(scores: Gb74Scores): RoleFitness[] {
  const axis = getAxisScores(scores);
  const f = (key: string) => {
    const factor = BIG5_FACTORS.find((x) => x.key === key);
    return factor ? getFactorScore(scores, factor) : 0;
  };
  // GBスキルの論理性（SK-01〜04 の平均 1〜5）
  const logic = () => getLogicScore(scores).score;
  // 因子 1〜5 → 0〜1、軸 0〜12 → 0〜1
  const n5 = (v: number) => Math.max(0, Math.min(1, (v - 1) / 4));
  const nAxis = (v: number) => Math.max(0, Math.min(1, v / AXIS_MAX));
  const factorValue = (key: string) => (key === 'LOGIC' ? n5(logic()) : n5(f(key)));

  return ROLE_DEFINITIONS.map((d) => ({
    name: d.name,
    score: Math.round(
      (ROLE_WEIGHT_PRIMARY * factorValue(d.primary) +
        ROLE_WEIGHT_SECONDARY * factorValue(d.secondary) +
        ROLE_WEIGHT_AXIS * nAxis(axis[d.axis])) *
        100
    ),
  })).sort((a, b) => b.score - a.score);
}

/** Shadow (気をつけたいこと、仮ロジック: スコアの高い上位2項目) */
export function getShadowItems(scores: Gb74Scores): Array<{ title: string; text: string }> {
  return SHADOW_QUESTIONS.map((s) => ({ ...s, value: scores.big5?.[s.code] ?? 0 }))
    .filter((s) => s.value >= 3) // 「たまにある」以上を兆候とみなす
    .sort((a, b) => b.value - a.value)
    .slice(0, 2)
    .map(({ title, text }) => ({ title, text }));
}

/** 軸別スタイル文 (仮テンプレート: 主軸ベース。1軸/3軸/対角など正本に思考・行動文が無いパターンの補完に使う) */
const AXIS_STYLE: Record<AxisKey, { think: string; act: string; scene: string; energy: string }> = {
  A: {
    think:
      '「まず仕組み」で考え始め、手順や段取りから状況を整理します。全体の流れを設計してから動くタイプです。',
    act: '計画を立て、順序立てて着実に進めるスタイルです。土台を固めながら一歩ずつ前進します。',
    scene: '業務プロセスの整備、品質の安定化、仕組み化が必要な場面で最も力を発揮します。',
    energy: '「仕組みが回り始めた」「ムダがなくなった」という手応えがエネルギー源です。',
  },
  B: {
    think:
      '「まず確実性」で考え始め、リスクや前例を踏まえて慎重に判断します。守るべきものを見極めるタイプです。',
    act: '決まったやり方で確実に進めるスタイルです。安定した品質を保ち続ける力があります。',
    scene: '安定運用、品質保証、堅実な実行が求められる場面で最も力を発揮します。',
    energy: '「滞りなく回っている」「ミスなくやり切った」という手応えがエネルギー源です。',
  },
  C: {
    think:
      '「まず人」で考え始め、関係性やチームの空気から状況を読み取ります。人を起点に発想するタイプです。',
    act: '周囲を巻き込みながら進めるスタイルです。声をかけ合いながら進める方が力を発揮します。',
    scene: 'チームの雰囲気づくり、外部との連携、人の力を引き出す場面で最も輝きます。',
    energy: '「人が動き出した」「チームがまとまった」という手応えが最大のエネルギー源です。',
  },
  D: {
    think:
      '「まず変化」で考え始め、これからの可能性やひらめきをもとに発想します。現状を変える視点を持つタイプです。',
    act: '新しいやり方を試しながら一気に動くスタイルです。前例にとらわれず突破口を開きます。',
    scene: '新しいプロジェクトの立ち上げ、改革、突破口が必要な場面で最も輝きます。',
    energy: '「新しいことが動き出した」「変化が生まれた」という手応えが最大のエネルギー源です。',
  },
};

/** 軸別スタイル文 (主軸ベースの4カード)。後方互換のため公開維持 */
export function getStyleTexts(primaryAxis: AxisKey): Array<{ title: string; text: string }> {
  const s = AXIS_STYLE[primaryAxis];
  return [
    { title: '考え方の傾向', text: s.think },
    { title: '動き方の傾向', text: s.act },
    { title: '強みが活きる場面', text: s.scene },
    { title: 'エネルギー源', text: s.energy },
  ];
}

/**
 * §03用スタイル4カードを構築する。
 * 思考スタイル/行動スタイルは正本04のコントラスト文言を優先し、
 * 提供されないパターン(1軸/3軸/対角)は軸別テンプレートで補完する。
 * 強みが活きる場面/エネルギー源は軸別テンプレートで補足する。
 */
function buildStyles(
  primaryAxis: AxisKey,
  sections: { thinking?: string; acting?: string }
): Array<{ title: string; text: string }> {
  const base = AXIS_STYLE[primaryAxis];
  return [
    { title: '思考スタイル', text: sections.thinking ?? base.think },
    { title: '行動スタイル', text: sections.acting ?? base.act },
    { title: '強みが活きる場面', text: base.scene },
    { title: 'エネルギー源', text: base.energy },
  ];
}

/** 補完の相手の詳細 (DB desc21〜25。共鳴/刺激の2ブロック) */
export interface ComplementDetail {
  /** 見出し (desc21: あなたを輝かせる人は) */
  heading: string;
  items: Array<{ label: string; theme: string; text: string }>;
}

/** レポート全体の派生データ */
export interface Gb74ReportData {
  axis: Record<AxisKey, number>;
  type: { name: string; sub: string; primaryAxis: AxisKey };
  /** パターンキー (gb74ContrastText) */
  patternKey: string;
  /** 分類カテゴリ */
  patternCategory: PatternCategory;
  /** コントラスト水準 (鮮明=vivid / 穏やか=gentle) */
  contrast: ContrastLevel;
  /** DB (judgment_patterns) を情報源にしたか。false=算出/テンプレ由来 */
  fromDb: boolean;
  /** タグライン (DB=desc13 / 非DB時は正本04) */
  tagline?: string;
  /** タイプ別の気をつけたいこと (正本04。DB結線時は未使用) */
  caution?: string;
  /** 補完の相手 (非DB時のフォールバック文字列。正本04) */
  complement?: string;
  /** 補完の相手 詳細 (DB desc21〜25) */
  complementDetail?: ComplementDetail;
  logic: { score: number; rank: 'good' | 'normal' | 'low' };
  factors: Array<{ factor: Big5Factor; score: number }>;
  strengths: Array<{ factor: Big5Factor; score: number }>;
  roles: RoleFitness[];
  shadows: Array<{ title: string; text: string }>;
  styles: Array<{ title: string; text?: string; theme?: string }>;
}

/**
 * scores (gb74) からレポートデータ一式を算出。gb74でなければ null。
 *
 * @param raw     gb74 scores
 * @param pattern DB (judgment_patterns) の該当パターン。findPatternByScores で取得。
 *                指定時は後半の文言節 (タイプ名/思考・行動/気をつけたい/補完) を
 *                **DB (desc11〜47) を唯一の情報源として** 差し替える (正本04/テンプレは不使用)。
 *                undefined 時は従来どおり正本04/テンプレで算出 (デモ・DB未取得時のフォールバック)。
 */
export function buildGb74Report(
  raw: unknown,
  pattern?: JudgmentPattern | null
): Gb74ReportData | null {
  const scores = unwrapGb74Scores(raw);
  if (!scores) return null;
  const axis = getAxisScores(scores);
  const classified = classifyPattern(axis);
  const contrast = getContrastLevel(axis);
  const sections = getContrastSections(classified.key, contrast) ?? {};
  const factors = getFactorScores(scores);

  const base = {
    axis,
    patternKey: classified.key,
    patternCategory: classified.category,
    contrast,
    logic: getLogicScore(scores),
    factors,
    strengths: factors.slice(0, 3),
    roles: getRoleFitness(scores),
  };

  // ===== DB結線: pattern があれば desc11〜47 を唯一源に文言節を構築 =====
  if (pattern) {
    const s = (v?: string | null) => (v ?? '').trim();
    const complementItems = [
      { label: '共鳴しやすいタイプ', theme: s(pattern.desc22), text: s(pattern.desc23) },
      { label: '刺激を受けやすいタイプ', theme: s(pattern.desc24), text: s(pattern.desc25) },
    ].filter((i) => i.theme || i.text);

    return {
      ...base,
      type: {
        name: s(pattern.desc11) || classified.typeName,
        sub: s(pattern.desc12) || classified.sub,
        primaryAxis: classified.primaryAxis,
      },
      fromDb: true,
      tagline: s(pattern.desc13) || undefined,
      caution: undefined,
      complement: undefined,
      complementDetail:
        s(pattern.desc21) || complementItems.length
          ? { heading: s(pattern.desc21), items: complementItems }
          : undefined,
      // 03 思考と行動のスタイル (desc13/14=思考, desc15/16=行動)
      styles: [
        { title: '思考スタイル', theme: s(pattern.desc13), text: s(pattern.desc14) },
        { title: '行動スタイル', theme: s(pattern.desc15), text: s(pattern.desc16) },
      ].filter((st) => st.theme || st.text),
      // 05 気をつけたいこと (desc34/35=やってしまいがち, desc44/45=弱み克服)
      shadows: [
        { title: s(pattern.desc34) || '業務上やってしまいがちなこと', text: s(pattern.desc35) },
        { title: s(pattern.desc44) || '弱みを克服するポイント', text: s(pattern.desc45) },
      ].filter((sh) => sh.text),
    };
  }

  // ===== フォールバック: 正本04 / 算出テンプレ (DB未取得・非gb74デモ用) =====
  let complement = sections.complement;
  if (!complement && classified.lowAxis) {
    complement = `${AXIS_META[classified.lowAxis].label} が高い人`;
  }
  return {
    ...base,
    type: { name: classified.typeName, sub: classified.sub, primaryAxis: classified.primaryAxis },
    fromDb: false,
    tagline: sections.tagline,
    caution: sections.caution,
    complement,
    styles: buildStyles(classified.primaryAxis, sections),
    shadows: getShadowItems(scores),
  };
}
